import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useApexTheme, mergeThemeOptions } from '../utils/apexTheme';
import { createHistogramBins } from '../utils/backtestCalculations';

const TradePnLHistogram = ({ trades, height = 250 }) => {
  const { baseThemeOptions, colors, isDark } = useApexTheme();

  const { options, series, bins } = useMemo(() => {
    if (!trades || trades.length === 0) return { options: null, series: [], bins: [] };

    const pnlValues = trades.map(t => t.pnl);
    const histogramBins = createHistogramBins(pnlValues, 15);

    const data = histogramBins.map(bin => ({
      x: '₹' + ((bin.min + bin.max) / 2).toFixed(0),
      y: bin.count,
      fillColor: (bin.min + bin.max) / 2 >= 0 ? (colors.success || '#4caf50') : (colors.error || '#f44336')
    }));

    const componentOptions = {
      chart: {
        type: 'bar',
        height,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          borderRadius: 2,
          columnWidth: '85%',
          distributed: true
        }
      },
      dataLabels: {
        enabled: false
      },
      colors: data.map(d => d.fillColor),
      legend: {
        show: false
      },
      xaxis: {
        categories: data.map(d => d.x),
        title: {
          text: 'Trade PnL (₹)'
        },
        labels: {
          rotate: -45,
          rotateAlways: true,
          style: {
            fontSize: '10px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Number of Trades'
        },
        labels: {
          formatter: (val) => Math.round(val)
        }
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const bin = histogramBins[dataPointIndex];
          if (!bin) return '';
          return `<div class="p-2 ${isDark ? 'bg-base-300' : 'bg-base-100'} rounded shadow">
            <b>PnL Range</b><br/>
            ₹${bin.min.toFixed(0)} to ₹${bin.max.toFixed(0)}<br/>
            Trades: ${bin.count}
          </div>`;
        }
      },
      annotations: {
        xaxis: [{
          x: '₹0',
          borderColor: colors.grid || '#999',
          strokeDashArray: 0
        }]
      }
    };

    return {
      options: mergeThemeOptions(baseThemeOptions, componentOptions),
      series: [{
        name: 'Trade PnL',
        data: data.map(d => d.y)
      }],
      bins: histogramBins
    };
  }, [trades, height, baseThemeOptions, colors, isDark]);

  if (!options) {
    return (
      <div className="flex items-center justify-center text-base-content/60" style={{ height }}>
        No trade data available
      </div>
    );
  }

  return (
    <div>
      <h4 className="text-sm font-bold mb-2">Trade PnL Distribution</h4>
      <Chart
        options={options}
        series={series}
        type="bar"
        height={height}
      />
    </div>
  );
};

export default TradePnLHistogram;
